/**
 * The Plans page's review-status ribbon: Open → In Review → Staffed, left to right as a
 * plan moves through staffing. Each tile carries its count and a bar for its share of the
 * plans in view; clicking one filters the list below to that group, clicking it again
 * clears it. Grouping lives in `@/lib/plan-review-stage`.
 */

import { CheckCircle2, ChevronRight, Layers, PencilLine, UserSearch } from "lucide-react";
import { Fragment } from "react";
import {
  PLAN_REVIEW_STAGE_COPY,
  PLAN_REVIEW_STAGES,
  type PlanReviewCounts,
  type PlanReviewFilter,
  type PlanReviewStage,
} from "@/lib/plan-review-stage";
import { cn } from "@/lib/utils";

/** Same ramp as the Resourcing status badge: Not set muted, In Review info, Staffed success. */
const STAGE_VISUAL: Record<
  PlanReviewStage,
  { icon: typeof CheckCircle2; iconWrap: string; label: string; bar: string; activeBg: string }
> = {
  open: {
    icon: PencilLine,
    iconWrap: "bg-muted text-muted-foreground",
    label: "text-muted-foreground",
    bar: "bg-muted-foreground/50",
    activeBg: "bg-muted/60",
  },
  inReview: {
    icon: UserSearch,
    iconWrap: "bg-info-100 text-info-700",
    label: "text-info-700",
    bar: "bg-info-500",
    activeBg: "bg-info-50",
  },
  staffed: {
    icon: CheckCircle2,
    iconWrap: "bg-success-100 text-success-700",
    label: "text-success-700",
    bar: "bg-success-500",
    activeBg: "bg-success-50",
  },
};

interface PlanReviewTilesProps {
  /** `null` while the plans are loading. */
  counts: PlanReviewCounts | null;
  active: PlanReviewFilter;
  onSelect: (filter: PlanReviewFilter) => void;
}

export function PlanReviewTiles({ counts, active, onSelect }: PlanReviewTilesProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr_1fr] sm:items-stretch sm:gap-2">
      {PLAN_REVIEW_STAGES.map((stage, i) => (
        <Fragment key={stage}>
          {i > 0 && (
            <ChevronRight
              aria-hidden
              className="hidden size-4 self-center text-muted-foreground/60 sm:block"
            />
          )}
          <ReviewTile
            stage={stage}
            count={counts ? counts[stage] : null}
            total={counts?.total ?? 0}
            rejected={stage === "open" ? (counts?.rejected ?? 0) : 0}
            active={active === stage}
            onSelect={() => onSelect(stage)}
          />
        </Fragment>
      ))}
      <AllPlansTile
        count={counts?.total ?? null}
        active={active === "all"}
        onSelect={() => onSelect("all")}
      />
    </div>
  );
}

function ReviewTile({
  stage,
  count,
  total,
  rejected,
  active,
  onSelect,
}: {
  stage: PlanReviewStage;
  count: number | null;
  total: number;
  rejected: number;
  active: boolean;
  onSelect: () => void;
}) {
  const { label, hint } = PLAN_REVIEW_STAGE_COPY[stage];
  const visual = STAGE_VISUAL[stage];
  const Icon = visual.icon;
  const share = count !== null && total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={`${label}: ${count ?? "loading"} ${count === 1 ? "plan" : "plans"}`}
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left transition",
        active ? cn("ring-2 ring-ring", visual.activeBg) : "hover:bg-muted/40",
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-full",
            visual.iconWrap,
          )}
        >
          <Icon className="size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              "truncate font-mono text-xs font-medium tracking-[0.08em] uppercase",
              visual.label,
            )}
          >
            {label}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {hint}
            {rejected > 0 && ` · incl. ${rejected} rejected`}
          </span>
        </div>
        <span className="font-display text-2xl font-bold leading-none text-heading">
          {count ?? "—"}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className={cn("h-full rounded-full transition-all", visual.bar)}
          style={{ width: `${share}%` }}
        />
      </div>
    </button>
  );
}

/** Sits after the stages, apart from the flow: every plan in view, whatever its stage. */
function AllPlansTile({
  count,
  active,
  onSelect,
}: {
  count: number | null;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      aria-label={`All Plans: ${count ?? "loading"} ${count === 1 ? "plan" : "plans"}`}
      className={cn(
        "flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition sm:ml-2",
        active ? "bg-brand-50 ring-2 ring-ring" : "hover:bg-muted/40",
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
        <Layers className="size-5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-mono text-xs font-medium tracking-[0.08em] text-brand-700 uppercase">
          All Plans
        </span>
        <span className="truncate text-xs text-muted-foreground">Every stage</span>
      </div>
      <span className="font-display text-2xl font-bold leading-none text-heading">
        {count ?? "—"}
      </span>
    </button>
  );
}
